import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { ProtectionGauge } from "@/components/ProtectionGauge";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { CalendarCheck, Loader2, Download, RefreshCw, AlertTriangle, DollarSign, Sparkles } from "lucide-react";
import { AIText } from "@/components/AIText";

const money = (n) => (n || n === 0 ? `$${Number(n).toLocaleString()}` : "—");

export default function Checkup() {
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/checkup?language=${lang}`).then((r) => setData(r.data)).catch(() => toast.error("Could not load checkup")).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportPdf = () => {
    if (!data) return;
    const gaps = data.gaps.map((g) => `- ${g.label}: ${g.reason}`).join("\n") || "No major gaps detected.";
    const renewals = data.upcoming_renewals.map((r) => `- ${r.category} (${r.carrier || "—"}): ${r.renewal_date} — ${r.days_until} days`).join("\n") || "None in the next year.";
    const reminders = data.open_reminders.map((r) => `- ${r.title} (${r.due_date})`).join("\n") || "None.";
    downloadReport({
      title: "Annual Protection Report", language: lang,
      subtitle: `Protection Score: ${data.protection.score}/100`,
      meta: { "Policies": data.policy_count, "Documents": data.document_count,
        "Monthly premiums": money(data.totals.monthly_premiums), "Annual premiums": money(data.totals.annual_premiums),
        "Largest deductible": money(data.totals.largest_deductible), "Coverage gaps": data.gaps.length },
      sections: [
        { heading: "Summary", body: data.summary },
        { heading: "Upcoming renewals (next 12 months)", body: renewals },
        { heading: "Potential coverage gaps", body: gaps },
        { heading: "Open reminders", body: reminders },
      ],
    });
  };

  return (
    <AppLayout>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><CalendarCheck className="h-7 w-7 text-brandblue" /> {t("checkup_title")}</h1>
          <p className="mt-1 text-slate-500">{t("checkup_sub")}</p>
        </div>
        {data && <Button data-testid="checkup-export" onClick={exportPdf} className="rounded-full brand-gradient text-white"><Download className="h-4 w-4 mr-2" />{t("download_report")}</Button>}
      </div>

      {loading ? (
        <div className="mt-10 flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> {t("preparing_checkup")}</div>
      ) : data && (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6 flex flex-col items-center">
            <ProtectionGauge score={data.protection.score} size={160} />
            <div className="mt-4 grid grid-cols-2 gap-3 w-full text-center">
              <Mini label={t("policies")} value={data.policy_count} />
              <Mini label={t("gaps")} value={data.gaps.length} accent={data.gaps.length > 0} />
              <Mini label={t("monthly")} value={money(data.totals.monthly_premiums)} />
              <Mini label={t("annual")} value={money(data.totals.annual_premiums)} />
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
              <h2 className="font-display text-lg font-semibold text-navy flex items-center gap-2"><Sparkles className="h-5 w-5 text-brandteal" /> {t("your_review")}</h2>
              <p className="mt-2"><AIText>{data.summary}</AIText></p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
                <h3 className="font-semibold text-navy flex items-center gap-2"><RefreshCw className="h-4 w-4 text-brandblue" /> {t("upcoming_renewals")}</h3>
                <div className="mt-3 space-y-2">
                  {data.upcoming_renewals.length === 0 && <p className="text-sm text-slate-400">{t("none_next_year")}</p>}
                  {data.upcoming_renewals.map((r, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="capitalize text-navy">{r.category} · {r.carrier || "—"}</span>
                      <span className={`text-xs ${r.days_until < 30 ? "text-amber-600 font-medium" : "text-slate-400"}`}>{r.days_until}d</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-3xl bg-white border border-slate-100 card-shadow p-6">
                <h3 className="font-semibold text-navy flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> {t("potential_gaps")}</h3>
                <div className="mt-3 space-y-2">
                  {data.gaps.length === 0 && <p className="text-sm text-slate-400">{t("no_major_gaps")} 🎉</p>}
                  {data.gaps.map((g, i) => (
                    <div key={i} className="text-sm"><span className="font-medium text-navy">{g.label}</span><p className="text-xs text-slate-400">{g.reason}</p></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

const Mini = ({ label, value, accent }) => (
  <div className="rounded-xl bg-slate-50 p-3">
    <p className={`font-display text-xl font-semibold ${accent ? "text-amber-600" : "text-navy"}`}>{value}</p>
    <p className="text-xs text-slate-400">{label}</p>
  </div>
);
